import React, { useCallback, useContext, useMemo, useState } from "react";
import { graphql, QueryRenderer } from "react-relay";
import Select, { type SingleValue } from "react-select";

import { faExclamationCircle, faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { omit, some } from "lodash";

import Alert from "~/common/Alert";
import AppContext from "~/common/AppContext";
import UserMultiSelector, {
	type MultiValueType,
	type UserMultiSelectorOption,
} from "~/common/UserSelect/UserMultiSelector";
import UserSelector, { type SingleValueType, type UserSelectorOption } from "~/common/UserSelect/UserSelector";
import { commitMutationPromise, useLazyLoadQuery } from "~/lib/relayUtil";
import { listFormat } from "~/lib/utils";
import { environment } from "~/models";
import TeamInput from "~/team/components/TeamInput";
import { Button, Tooltip } from "~/ui";
import Popup from "~/ui/legacy/Popup";
import { HelpCenterIcon, Modal, TextField } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import reactSelect from "~/ui/react-select";

import type { CreateOrEditTeamCreateMutation } from "~/__generated__/CreateOrEditTeamCreateMutation.graphql";
import type { CreateOrEditTeamDirectReportsQuery as DirectReportsQueryType } from "~/__generated__/CreateOrEditTeamDirectReportsQuery.graphql";
import type { CreateOrEditTeamEditMutation } from "~/__generated__/CreateOrEditTeamEditMutation.graphql";
import type { CreateOrEditTeamGoogleWorkspaceGroupsQuery as GoogleWorkspaceGroupsQueryType } from "~/__generated__/CreateOrEditTeamGoogleWorkspaceGroupsQuery.graphql";
import type { CreateOrEditTeamGroupUsersQuery as GroupUsersQueryType } from "~/__generated__/CreateOrEditTeamGroupUsersQuery.graphql";
import type { CreateOrEditTeamOffice365GroupsQuery as Office365GroupsQueryType } from "~/__generated__/CreateOrEditTeamOffice365GroupsQuery.graphql";
import type { teamQuery_Info$data as Team } from "~/__generated__/teamQuery_Info.graphql";

import styles from "./CreateOrEditTeam.module.css";

const createTeamMutation = graphql`
	mutation CreateOrEditTeamCreateMutation(
		$title: String!
		$handle: String
		$description: String
		$location: String
		$parentId: ID
		$members: [String!]
		$syncFromManagerId: ID
		$syncFromExternalGroupId: String
	) {
		createTeam(
			title: $title
			handle: $handle
			description: $description
			location: $location
			parentId: $parentId
			members: $members
			syncFromManagerId: $syncFromManagerId
			syncFromExternalGroupId: $syncFromExternalGroupId
		) {
			team {
				...teamQuery_Info @relay(mask: false)
				...TeamLink_info
			}
		}
	}
`;

export const editTeamMutation = graphql`
	mutation CreateOrEditTeamEditMutation(
		$id: ID!
		$title: String!
		$handle: String
		$description: String
		$location: String
		$parentId: ID
		$members: [String!]
		$syncFromManagerId: ID
		$syncFromExternalGroupId: String
	) {
		updateTeam(
			id: $id
			title: $title
			handle: $handle
			description: $description
			location: $location
			parentId: $parentId
			members: $members
			syncFromManagerId: $syncFromManagerId
			syncFromExternalGroupId: $syncFromExternalGroupId
		) {
			team {
				...teamQuery_Info @relay(mask: false)
			}
		}
	}
`;

const googleWorkspaceGroupsQuery = graphql`
	query CreateOrEditTeamGoogleWorkspaceGroupsQuery {
		account {
			integrations {
				gsuite {
					googleWorkspaceGroups {
						id
						externalId
						name
					}
				}
			}
		}
	}
`;

const office365GroupsQuery = graphql`
	query CreateOrEditTeamOffice365GroupsQuery {
		account {
			integrations {
				office365 {
					office365Groups {
						id
						externalId
						name
					}
				}
			}
		}
	}
`;

const groupUsersQuery = graphql`
	query CreateOrEditTeamGroupUsersQuery($groupId: ID!) {
		node(id: $groupId) {
			... on GoogleGroup {
				users {
					fullName
				}
			}
			... on Office365Group {
				users {
					fullName
				}
			}
		}
	}
`;

const directReportsQuery = graphql`
	query CreateOrEditTeamDirectReportsQuery($managerId: ID!) {
		viewer {
			usersById(userIds: [$managerId]) {
				fullName
				internalAttributes {
					directReports {
						edges {
							node {
								fullName
							}
						}
					}
				}
			}
		}
	}
`;

enum Alerts {
	TITLE_ERROR,
	HANDLE_ERROR,
}

export enum TeamEditType {
	DETAILS,
	MEMBERS,
}

export enum TeamSyncType {
	MANUAL = "manual",
	MANAGER_HIERARCHY = "managerHierarchy",
	EXTERNAL = "external",
}

interface CreateOrEditTeamProps {
	team?: Omit<Team, " $fragmentType">;
	editType?: TeamEditType;
	onClose(): void;
	onCreated?(teamId: string): void;
}

type OptionType = {
	id: string;
	label: string;
	value: string;
};

const initialTeam = (user: Team["members"][0] | undefined) => {
	return {
		id: "",
		title: "",
		description: "",
		members: user ? [{ ...user }] : [],
		parent: null,
		sync: null,
		handle: "",
		location: "",
		archived: false,
	};
};

export default function CreateOrEditTeam({ team: editTeam, editType, onClose, onCreated }: CreateOrEditTeamProps) {
	const { user, account } = useContext(AppContext);
	// @ts-ignore
	const [team, setTeam] = useState<Omit<Team, " $fragmentType">>(editTeam ? editTeam : initialTeam(user));

	const [selectedTeamMembers, setSelectedTeamMembers] = useState<UserMultiSelectorOption[]>(
		team.members.map(member => ({
			value: member.id,
			label: member.fullName,
			avatar: {
				id: member.id,
				fullName: member.fullName,
				isGuest: false,
				image: member.image || "",
				email: member.email || "",
			},
		})),
	);

	// Derive team member IDs from selectedTeamMembers
	const teamMemberIds: string[] = useMemo(() => selectedTeamMembers.map(member => member.value), [selectedTeamMembers]);

	const [selectedManager, setSelectedManager] = useState<UserSelectorOption | null>(
		team.sync?.fromManager
			? {
					value: team.sync.fromManager.id,
					label: team.sync.fromManager.fullName,
					avatar: {
						id: team.sync.fromManager.id,
						fullName: team.sync.fromManager.fullName,
						isGuest: false,
						image: "",
					},
				}
			: null,
	);

	const syncFromManagerId = selectedManager?.value;
	const [syncFromExternalGroupId, setSyncFromExternalGroupId] = useState(team.sync?.fromExternalGroupId);
	const [isHandleManuallySet, setIsHandleManuallySet] = useState(!!editTeam);
	const [editPhase, setEditPhase] = useState<TeamEditType>(editType ?? TeamEditType.DETAILS);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [alert, setAlert] = useState<Alerts | null>(null);
	const [alertMessage, setAlertMessage] = useState("");
	const [teamSyncType, setTeamSyncType] = useState<TeamSyncType>(
		syncFromManagerId
			? TeamSyncType.MANAGER_HIERARCHY
			: syncFromExternalGroupId
				? TeamSyncType.EXTERNAL
				: TeamSyncType.MANUAL,
	);

	const isManualTeam = teamSyncType === TeamSyncType.MANUAL;
	const isManagerTeam = teamSyncType === TeamSyncType.MANAGER_HIERARCHY;
	const isExternalTeam = teamSyncType === TeamSyncType.EXTERNAL;

	const setHandle = useCallback(
		(newHandle: string) => {
			const cutHandle = newHandle.replace(/\W+/gi, "").substring(0, 128);
			setTeam(team => ({ ...team, handle: cutHandle }));
			if (alert === Alerts.HANDLE_ERROR) {
				setAlert(null);
				setAlertMessage("");
			}
		},
		[alert, setAlert, setAlertMessage],
	);

	const onChangeTitle = useCallback(
		(newTitle: string) => {
			const cutTitle = newTitle.substring(0, 128);
			setTeam(team => ({ ...team, title: cutTitle }));

			if (cutTitle && alert === Alerts.TITLE_ERROR) {
				setAlert(null);
				setAlertMessage("");
			}

			if (!isHandleManuallySet) {
				setHandle(cutTitle.toLowerCase());
			}
		},
		[alert, isHandleManuallySet, setHandle],
	);

	const onChangeDescription = useCallback(
		(newDescription: string) => {
			setTeam({ ...team, description: newDescription });
		},
		[team],
	);

	const onChangeMembers = useCallback((newMembers: MultiValueType<UserMultiSelectorOption>) => {
		setSelectedTeamMembers([...newMembers]);
	}, []);

	const onChangeManager = useCallback((manager: SingleValueType<UserSelectorOption>) => {
		setSelectedManager(manager);
	}, []);

	const onChangeExternalGroup = useCallback(
		(externalGroupId: string) => {
			setSyncFromExternalGroupId(externalGroupId);
		},
		[setSyncFromExternalGroupId],
	);

	const onChangeParent = useCallback(
		(newParent?: string) => {
			setTeam({ ...team, parent: newParent ? { id: newParent, title: "" } : null });
		},
		[team],
	);

	const onChangeHandle = useCallback(
		(newHandle: string) => {
			setIsHandleManuallySet(true);
			setHandle(newHandle);
		},
		[setIsHandleManuallySet, setHandle],
	);

	const onChangeLocation = useCallback(
		(newLocation: string) => {
			setTeam({ ...team, location: newLocation.substring(0, 256) });
		},
		[team],
	);

	const isValid = useCallback(() => {
		if (!team.title) {
			setAlert(Alerts.TITLE_ERROR);
			return false;
		}
		return true;
	}, [team]);

	const checkAndSetErrors = useCallback((reasons: [{ message: string }]) => {
		if (!Array.isArray(reasons)) {
			return;
		}
		for (let i = 0; i < reasons.length; i++) {
			let reason;
			try {
				reason = JSON.parse(reasons[i].message.replace(/"/gi, "`").replace(/'/gi, '"'));
			} catch (err) {
				continue;
			}
			if (!reason) {
				continue;
			}
			if (reason.title) {
				setAlert(Alerts.TITLE_ERROR);
				const message = Array.isArray(reason.title) ? reason.title[0] : reason.title;
				setAlertMessage(`Title: ${message}`);
				return;
			}
			if (reason.handle) {
				setAlert(Alerts.HANDLE_ERROR);
				const message = Array.isArray(reason.handle) ? reason.handle[0] : reason.handle;
				setAlertMessage(`Handle: ${message}`);
				return;
			}
		}
	}, []);

	const onCreate = useCallback(() => {
		if (!isValid()) {
			return;
		}

		if (editPhase === TeamEditType.DETAILS) {
			setEditPhase(TeamEditType.MEMBERS);
			return;
		}

		setIsLoading(true);
		commitMutationPromise<CreateOrEditTeamCreateMutation>(environment, {
			mutation: createTeamMutation,
			variables: isManualTeam
				? {
						...omit(team, ["id", "archived", "sync", "parent"]),
						parentId: team.parent?.id,
						members: teamMemberIds,
					}
				: isManagerTeam
					? {
							...omit(team, ["id", "archived", "sync", "parent"]),
							parentId: team.parent?.id,
							syncFromManagerId: syncFromManagerId,
							members: [],
						}
					: {
							...omit(team, ["id", "archived", "sync", "parent"]),
							parentId: team.parent?.id,
							syncFromExternalGroupId: syncFromExternalGroupId,
							members: [],
						},
			updater: (store, payload) => {
				if (!payload) return;
				const viewer = store.get("viewer");
				const myTeams = viewer?.getLinkedRecords("myTeams");
				if (!myTeams) {
					return;
				}
				const createdTeam = payload.createTeam?.team;
				if (!createdTeam) {
					return;
				}
				const newRecord = store.get(createdTeam?.id);
				if (!newRecord) {
					return;
				}

				if (myTeams) {
					const userIsAMember = some(createdTeam.members, { id: user?.id });
					if (!userIsAMember) {
						return;
					}
					const newTeams = [...myTeams, newRecord];
					viewer?.setLinkedRecords(newTeams, "myTeams");
				}
			},
			onCompleted: response => {
				setIsLoading(false);
				if (response.createTeam?.team?.id) {
					if (onCreated) onCreated(response.createTeam.team.id);
					onClose();
				}
			},
		}).catch(reason => {
			checkAndSetErrors(reason);
		});
	}, [
		isValid,
		team,
		user,
		onCreated,
		onClose,
		checkAndSetErrors,
		editPhase,
		isManualTeam,
		isManagerTeam,
		syncFromManagerId,
		syncFromExternalGroupId,
		teamMemberIds,
	]);

	const onEdit = useCallback(() => {
		if (!isValid()) {
			return;
		}

		setIsLoading(true);
		commitMutationPromise<CreateOrEditTeamEditMutation>(environment, {
			mutation: editTeamMutation,
			variables:
				editPhase === TeamEditType.DETAILS
					? {
							...omit(team, [
								"archived",
								"hasAccess",
								"isAccountWideTeam",
								"members",
								"sync",
								"membersCount",
								"parent",
							]),
							parentId: team.parent?.id || null,
						}
					: {
							id: team.id,
							title: team.title,
							syncFromManagerId: team.sync === null ? null : syncFromManagerId,
							syncFromExternalGroupId: team.sync === null ? null : syncFromExternalGroupId,
							members: teamMemberIds,
							parentId: team.parent?.id || null,
						},
			updater: (store, payload) => {
				if (!payload) return;
				// Manually updates the my team list
				const viewer = store.get("viewer");
				const myTeams = viewer?.getLinkedRecords("myTeams");
				if (!myTeams) {
					return;
				}
				const updatedTeam = payload.updateTeam?.team;
				if (!updatedTeam) {
					return;
				}
				const teamRecord = store.get(updatedTeam?.id);
				if (!teamRecord) {
					return;
				}
				const myTeamsUserIndex = myTeams.findIndex(team => team.getDataID() === teamRecord.getDataID());
				const userWasAMember = myTeamsUserIndex >= 0;
				const userIsNowAMember = some(updatedTeam.members, { id: user?.id });
				if (!userWasAMember && userIsNowAMember) {
					const newTeams = [...myTeams, teamRecord];
					viewer?.setLinkedRecords(newTeams, "myTeams");
					return;
				}
				if (userWasAMember && !userIsNowAMember) {
					myTeams.splice(myTeamsUserIndex, 1);
					viewer?.setLinkedRecords(myTeams, "myTeams");
					return;
				}
			},
			onCompleted: response => {
				setIsLoading(false);
				if (response.updateTeam?.team) {
					onClose();
				}
			},
		}).catch(reason => {
			checkAndSetErrors(reason);
		});
	}, [
		isValid,
		team,
		user,
		onClose,
		checkAndSetErrors,
		editPhase,
		syncFromManagerId,
		syncFromExternalGroupId,
		teamMemberIds,
	]);

	const onBack = useCallback(() => {
		setEditPhase(TeamEditType.DETAILS);
	}, []);

	const cannotCreateTeam =
		!editTeam &&
		((isManagerTeam && !syncFromManagerId) || (isExternalTeam && !syncFromExternalGroupId)) &&
		editPhase !== TeamEditType.DETAILS;

	const isGoogleWorkspace = account?.integrations?.gsuite != null;
	const isGoogleWorkspaceConnected = account?.integrations?.gsuite?.delegated;
	const isOffice365Workspace = account?.integrations?.office365 != null;
	const isOffice365WorkspaceConnected = account?.integrations?.office365?.adminConsented;
	const isWorkspaceIntegrated = isGoogleWorkspaceConnected || isOffice365WorkspaceConnected;
	const isEditMode = !!editTeam;
	const disabledExternalGroupIntegrationTooltip = isGoogleWorkspace
		? "Workspace admins can set up the required Google workspace integration"
		: "Workspace admins can set up the required Microsoft 365 workspace integration";

	return (
		<Modal width="27.5rem" onClose={onClose}>
			<Modal.Header>
				<Modal.Title>{editTeam ? "Edit team" : "Create team"}</Modal.Title>
				<Modal.HeaderActions onClose={onClose}>
					<HelpCenterIcon articleId="5647930" title="How to create teams" />
				</Modal.HeaderActions>
			</Modal.Header>

			{alert !== null && alertMessage && (
				<Alert error icon={faExclamationCircle}>
					{alertMessage}
				</Alert>
			)}

			<Modal.Body className={styles.body}>
				{editPhase === TeamEditType.DETAILS && (
					<>
						<div className={styles.field}>
							<label>Name</label>
							<TextField
								value={team.title}
								setValue={onChangeTitle}
								placeholder="Ex. Engineering"
								className={cx({ [styles.fieldError]: alert === Alerts.TITLE_ERROR })}
							/>
						</div>
						<div className={styles.field}>
							<label>Team purpose</label>
							<TextField
								value={team.description || ""}
								setValue={onChangeDescription}
								placeholder="Write the purpose / mission for this team"
							/>
						</div>
						<div className={styles.field}>
							<label>Parent team</label>
							{/* Maybe we should have React.Suspense inside the team input component */}
							<React.Suspense fallback={<LoadingSpinner inline />}>
								<TeamInput
									selected={team.parent?.id}
									forTeam={editTeam ? team.id : undefined}
									onChange={onChangeParent}
									className={styles.parentSelect}
								/>
							</React.Suspense>
						</div>
						<div className={styles.additionalData}>
							<div className={cx(styles.field, styles.handleField)}>
								<label>
									Handle
									<Popup
										size="tiny"
										content={
											<p className={styles.infoText}>
												<strong>Coming soon!</strong>
												<br />
												We’re currently working on ways to use team handles in Fellow.
											</p>
										}
										position="top center"
										trigger={<FontAwesomeIcon icon={faInfoCircle} className={styles.infoIcon} />}
									/>
								</label>
								<div className={styles.handle}>
									<span>@</span>
									<TextField
										value={team.handle || ""}
										setValue={onChangeHandle}
										placeholder="Team Handle"
										className={cx({ [styles.fieldError]: alert === Alerts.HANDLE_ERROR })}
									/>
								</div>
							</div>
							<div className={styles.field}>
								<label>Location</label>
								<TextField value={team.location || ""} setValue={onChangeLocation} placeholder="Team location" />
							</div>
						</div>
					</>
				)}
				{editPhase === TeamEditType.MEMBERS && (
					<>
						<p>Select team members, or sync team with a predefined group of users.</p>
						<div>
							<div className={styles.teamSyncType}>
								<label className={cx({ [styles.disabled]: isEditMode && !isManualTeam })}>
									<input
										type="radio"
										disabled={isEditMode}
										className={styles.radioInput}
										name="teamSyncType"
										value={TeamSyncType.MANUAL}
										checked={isManualTeam}
										onChange={e => {
											setTeamSyncType(e.target.value as TeamSyncType);
										}}
									/>
									<div>
										Select users
										<p>Search and select users to add to the team</p>
									</div>
								</label>

								{isManualTeam && (
									<div className={styles.field}>
										<UserMultiSelector
											key="selectTeamMembers"
											ranked
											textOnly
											excludeGuests
											selectedUsers={selectedTeamMembers}
											lockedUsers={
												user && !user.internalAttributes?.isAccountAdmin
													? selectedTeamMembers.filter(member => member.value === user.id)
													: []
											}
											placeholder="Add one or more teammates by name or email"
											onChange={onChangeMembers}
											className={styles.userSelect}
											defaultOptions
										/>
									</div>
								)}
							</div>
							<div className={styles.teamSyncType}>
								<label className={cx({ [styles.disabled]: isEditMode && !isManagerTeam })}>
									<input
										type="radio"
										disabled={isEditMode}
										className={styles.radioInput}
										name="teamSyncType"
										value={TeamSyncType.MANAGER_HIERARCHY}
										checked={isManagerTeam}
										onChange={e => {
											setTeamSyncType(e.target.value as TeamSyncType);
										}}
									/>
									<div>
										Manager based team
										<p>Sync team members with a manager’s reports</p>
									</div>
								</label>

								{isManagerTeam && (
									<div>
										<UserSelector
											key="selectManager"
											excludeGuests
											excludeNonManagers
											selectedUser={selectedManager}
											placeholder="Add a manager by name or email"
											onChange={onChangeManager}
											className={styles.userSelect}
										/>
										{syncFromManagerId && <ManagerDirectReports managerId={syncFromManagerId} />}
									</div>
								)}
							</div>
							<Tooltip disabled={isWorkspaceIntegrated} content={disabledExternalGroupIntegrationTooltip}>
								<div className={cx([styles.teamSyncType, { [styles.disabled]: !isWorkspaceIntegrated }])}>
									<label className={cx({ [styles.disabled]: isEditMode && !isExternalTeam })}>
										<input
											type="radio"
											disabled={isEditMode || !isWorkspaceIntegrated}
											className={styles.radioInput}
											name="teamSyncType"
											value={TeamSyncType.EXTERNAL}
											checked={isExternalTeam}
											onChange={e => {
												setTeamSyncType(e.target.value as TeamSyncType);
											}}
										/>
										{isGoogleWorkspace && (
											<div>
												<span>Google Workspace based team</span>
												<p>Sync team members with a Google Workspace group</p>
											</div>
										)}
										{isOffice365Workspace && (
											<div>
												<span>Microsoft 365 based team</span>
												<p>Sync team members with a Microsoft 365 group</p>
											</div>
										)}
									</label>

									{isExternalTeam && (
										<div>
											{isGoogleWorkspace && isWorkspaceIntegrated && (
												<GoogleWorkspaceGroupSelector
													selectedGoogleWorkspaceGroupId={syncFromExternalGroupId || ""}
													setSelectedGoogleWorkspaceGroupId={onChangeExternalGroup}
												/>
											)}
											{isOffice365Workspace && isWorkspaceIntegrated && (
												<Office365GroupSelector
													selectedOffice365GroupId={syncFromExternalGroupId || ""}
													setSelectedOffice365GroupId={onChangeExternalGroup}
												/>
											)}
										</div>
									)}
								</div>
							</Tooltip>
						</div>
					</>
				)}
			</Modal.Body>
			<Modal.Footer>
				{!editTeam && editPhase === TeamEditType.MEMBERS && (
					<Modal.FooterSecondaryActions>
						<Button size="normal" variant="secondary" onClick={onBack}>
							Back
						</Button>
					</Modal.FooterSecondaryActions>
				)}
				<Button size="normal" variant="secondary" onClick={onClose}>
					Cancel
				</Button>
				<Button
					variant="primary"
					size="normal"
					onClick={editTeam ? onEdit : onCreate}
					disabled={isLoading || cannotCreateTeam}
				>
					{editTeam ? "Save" : editPhase === TeamEditType.DETAILS ? "Next" : "Create team"}
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
function groupSelector(
	selectGroupId: string | null,
	onGroupChange: (value: SingleValue<OptionType>) => void,
	groupOptions: Array<OptionType> | undefined,
	isLoading: boolean,
) {
	const selectedGroupOption = groupOptions?.find(option => option.value === selectGroupId);

	return (
		<div className={styles.groupSelector}>
			<Select
				placeholder="Select a group to sync with"
				isClearable={false}
				isLoading={isLoading}
				options={groupOptions}
				value={selectedGroupOption}
				onChange={onGroupChange}
				{...reactSelect}
			/>

			{selectedGroupOption && (
				<React.Suspense fallback={<LoadingSpinner dimmer={false} inline="centered" />}>
					<ExternalGroupMembers groupId={selectedGroupOption.id} />
				</React.Suspense>
			)}
		</div>
	);
}

function GoogleWorkspaceGroupSelector({
	selectedGoogleWorkspaceGroupId,
	setSelectedGoogleWorkspaceGroupId,
}: {
	selectedGoogleWorkspaceGroupId: string | null;
	setSelectedGoogleWorkspaceGroupId: (groupId: string) => void;
}) {
	const onGroupChange = React.useCallback(
		(value: SingleValue<OptionType>) => {
			setSelectedGoogleWorkspaceGroupId(value?.value ?? "");
		},
		[setSelectedGoogleWorkspaceGroupId],
	);
	const renderGoogleWorkspaceGroupSelector = React.useCallback(
		({ props }: { props: GoogleWorkspaceGroupsQueryType["response"] | null }) => {
			const groupOptions = (props?.account.integrations.gsuite?.googleWorkspaceGroups || []).map(group => ({
				id: group.id,
				value: group.externalId,
				label: group.name,
			}));
			const isLoading = props == null;

			return groupSelector(selectedGoogleWorkspaceGroupId, onGroupChange, groupOptions, isLoading);
		},
		[selectedGoogleWorkspaceGroupId, onGroupChange],
	);

	return (
		<QueryRenderer<GoogleWorkspaceGroupsQueryType>
			environment={environment}
			query={googleWorkspaceGroupsQuery}
			variables={{}}
			render={renderGoogleWorkspaceGroupSelector}
		/>
	);
}

function Office365GroupSelector({
	selectedOffice365GroupId,
	setSelectedOffice365GroupId,
}: {
	selectedOffice365GroupId: string | null;
	setSelectedOffice365GroupId: (groupId: string) => void;
}) {
	const onGroupChange = React.useCallback(
		(value: SingleValue<OptionType>) => {
			setSelectedOffice365GroupId(value?.value ?? "");
		},
		[setSelectedOffice365GroupId],
	);

	const renderOffice365GroupSelector = React.useCallback(
		({ props }: { props: Office365GroupsQueryType["response"] | null }) => {
			const groupOptions = (props?.account.integrations.office365?.office365Groups || []).map(group => ({
				id: group.id,
				value: group.externalId,
				label: group.name,
			}));
			const isLoading = props == null;

			return groupSelector(selectedOffice365GroupId, onGroupChange, groupOptions, isLoading);
		},
		[selectedOffice365GroupId, onGroupChange],
	);

	return (
		<QueryRenderer<Office365GroupsQueryType>
			environment={environment}
			query={office365GroupsQuery}
			variables={{}}
			render={renderOffice365GroupSelector}
		/>
	);
}

function ManagerDirectReports({ managerId }: { managerId: string }) {
	const renderManagerDirectReports = React.useCallback(
		({ props }: { props: DirectReportsQueryType["response"] | null }) => {
			const manager = props?.viewer.usersById?.length === 1 ? props?.viewer.usersById[0] : null;
			const directReportEdges = manager?.internalAttributes?.directReports?.edges || [];
			const directReportFullNames = [manager?.fullName ?? ""].concat(directReportEdges.map(edge => edge.node.fullName));

			return props && <DirectReportTeamMembers teamMembers={directReportFullNames} />;
		},
		[],
	);

	return (
		<QueryRenderer<DirectReportsQueryType>
			environment={environment}
			query={directReportsQuery}
			variables={{ managerId: managerId }}
			render={renderManagerDirectReports}
		/>
	);
}

function ExternalGroupMembers({ groupId }: { groupId: string }) {
	const data = useLazyLoadQuery<GroupUsersQueryType>(
		groupUsersQuery,
		{ groupId: groupId },
		{ fetchPolicy: "store-or-network" },
	);

	const userNames = data.node?.users?.map(user => user.fullName) ?? [];

	return (
		<div className={styles.teamMembers}>
			<b>Team members:</b>
			{userNames.length === 0 ? <p>This team has no members in the workspace.</p> : <p>{listFormat(userNames)}.</p>}
		</div>
	);
}

function DirectReportTeamMembers({ teamMembers }: { teamMembers: Array<string> }) {
	return (
		<div className={styles.teamMembers}>
			<b>Team members:</b>
			<p>{listFormat(teamMembers)}.</p>
		</div>
	);
}
